import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import useAppSettingsStore from "@/store/appSettingsStore";
import { toast } from "react-toastify";
import packageJson from "../../package.json";
import { getProvinces, getCities } from "@/services/province";
import { convertFarsiToEnglish } from "@/utils/numberConverter";
import { isPendingApprovalStatus } from "@/utils/auth";

const PHONE_REGEX = /^09\d{9}$/;
const MIN_OTP_LENGTH = 4;
const MAX_OTP_LENGTH = 8;
const DEFAULT_OTP_SECONDS = 120;
const REGISTER_SUCCESS_MESSAGE =
  "ثبت‌نام با موفقیت انجام شد. پس از تأیید توسط مدیر می‌توانید وارد شوید.";
const PENDING_APPROVAL_MESSAGE =
  "حساب شما در انتظار تأیید مدیر است. پس از تأیید می‌توانید وارد پنل معاملات شوید.";

function UserIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );
}

function LockIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );
}

function PhoneIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );
}

function EyeIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}

function EyeOffIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  );
}

function normalizePhoneInput(value) {
  return convertFarsiToEnglish(value).replace(/\D/g, "").slice(0, 11);
}

function normalizeOtpInput(value) {
  return convertFarsiToEnglish(value).replace(/\D/g, "").slice(0, MAX_OTP_LENGTH);
}

/** Parse server TimeSpan like "00:02:00" to seconds */
function parseExpireTimeToSeconds(expireTime) {
  if (expireTime == null || expireTime === "") return DEFAULT_OTP_SECONDS;
  if (typeof expireTime === "number" && Number.isFinite(expireTime)) {
    return expireTime > 1000 ? Math.round(expireTime / 1000) : Math.round(expireTime);
  }
  const str = String(expireTime).trim();
  const parts = str.split(":").map((p) => parseInt(p, 10));
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2 && parts.every((n) => Number.isFinite(n))) {
    return parts[0] * 60 + parts[1];
  }
  const n = parseInt(str, 10);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_OTP_SECONDS;
}

function formatTimer(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function isExpiredTokenError(message) {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("منقضی") ||
    m.includes("مصرف") ||
    m.includes("expire") ||
    m.includes("expired") ||
    m.includes("invalid token")
  );
}

export default function Login() {
  const { login, mobileRegister, mobileLogin, verifyLogin } = useAuth();
  const navigate = useNavigate();
  const { settings, fetchSettings } = useAppSettingsStore();

  // "login" | "otp" | "register"
  const [authMode, setAuthMode] = useState("login");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // OTP flow (state only — not localStorage)
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [verificationToken, setVerificationToken] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);
  const lastSubmittedOtpRef = useRef("");

  // Register
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [provinceId, setProvinceId] = useState("");
  const [cityId, setCityId] = useState("");
  const [address, setAddress] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [provincesLoaded, setProvincesLoaded] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (authMode !== "register" || provincesLoaded) return;

    let cancelled = false;
    const loadProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const list = await getProvinces();
        if (!cancelled) {
          setProvinces(list);
          setProvincesLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "دریافت لیست استان‌ها ناموفق بود");
        }
      } finally {
        if (!cancelled) setLoadingProvinces(false);
      }
    };
    loadProvinces();
    return () => {
      cancelled = true;
    };
  }, [authMode, provincesLoaded]);

  useEffect(() => {
    if (!provinceId) {
      setCities([]);
      return;
    }

    let cancelled = false;
    const loadCities = async () => {
      setLoadingCities(true);
      setCityId("");
      try {
        const list = await getCities(Number(provinceId));
        if (!cancelled) setCities(list);
      } catch (err) {
        if (!cancelled) {
          setCities([]);
          toast.error(err.message || "دریافت لیست شهرها ناموفق بود");
        }
      } finally {
        if (!cancelled) setLoadingCities(false);
      }
    };
    loadCities();
    return () => {
      cancelled = true;
    };
  }, [provinceId]);

  // OTP countdown — restart only when token appears or timer becomes active again
  const otpTimerActive = Boolean(verificationToken) && otpTimer > 0;
  useEffect(() => {
    if (!otpTimerActive) return undefined;
    const interval = setInterval(() => {
      setOtpTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimerActive]);

  const resetOtpFlow = () => {
    setVerificationToken(null);
    setOtpCode("");
    setOtpTimer(0);
    setError("");
    lastSubmittedOtpRef.current = "";
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setError("");
    if (mode !== "otp") resetOtpFlow();
  };

  const navigateAfterAuth = (userData) => {
    if (isPendingApprovalStatus(userData?.status)) {
      toast.info(PENDING_APPROVAL_MESSAGE);
      navigate("/pending-approval", { replace: true });
      return;
    }
    navigate("/", { replace: true });
  };

  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const userData = await login({ username, password });
      navigateAfterAuth(userData);
    } catch (err) {
      const errorMessage = err.message || "ورود ناموفق بود. نام کاربری یا رمز اشتباه است.";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const requestOtp = async ({ isResend = false } = {}) => {
    if (!PHONE_REGEX.test(otpPhone)) {
      const msg = "شماره موبایل باید با فرمت 09xxxxxxxxx باشد";
      toast.error(msg);
      setError(msg);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const response = await mobileLogin({ phoneNumber: otpPhone });
      if (!response?.verificationToken) {
        throw new Error("توکن تایید دریافت نشد");
      }
      setVerificationToken(response.verificationToken);
      setOtpTimer(parseExpireTimeToSeconds(response.expireTime));
      setOtpCode("");
      lastSubmittedOtpRef.current = "";
      toast.success(isResend ? "کد تایید مجدداً ارسال شد" : "کد تایید به شماره شما ارسال شد");
    } catch (err) {
      const errorMessage = err.message || "ارسال کد تایید ناموفق بود";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpPhoneSubmit = async (e) => {
    e.preventDefault();
    await requestOtp({ isResend: false });
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0 || submitting) return;
    await requestOtp({ isResend: true });
  };

  const submitOtpCode = async (code) => {
    if (!verificationToken) {
      setError("لطفا ابتدا کد تایید را دریافت کنید");
      return;
    }
    if (code.length < MIN_OTP_LENGTH) return;
    if (code === lastSubmittedOtpRef.current && submitting) return;

    lastSubmittedOtpRef.current = code;
    setSubmitting(true);
    setError("");
    try {
      const userData = await verifyLogin({ token: verificationToken, code });
      navigateAfterAuth(userData);
    } catch (err) {
      const errorMessage = err.message || "کد تایید نامعتبر است";
      toast.error(errorMessage);
      setError(errorMessage);
      if (isExpiredTokenError(errorMessage)) {
        resetOtpFlow();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < MIN_OTP_LENGTH) {
      const msg = `کد تایید حداقل ${MIN_OTP_LENGTH} رقم است`;
      toast.error(msg);
      setError(msg);
      return;
    }
    await submitOtpCode(otpCode);
  };

  // Auto-submit when 4+ digits entered
  useEffect(() => {
    if (
      authMode === "otp" &&
      verificationToken &&
      otpCode.length >= MIN_OTP_LENGTH &&
      otpCode.length <= MAX_OTP_LENGTH &&
      !submitting &&
      otpCode !== lastSubmittedOtpRef.current
    ) {
      // Only auto-submit at exactly 4 digits to avoid premature submit while typing longer codes
      if (otpCode.length === MIN_OTP_LENGTH) {
        submitOtpCode(otpCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fire on otpCode change
  }, [otpCode, verificationToken, authMode]);

  const validateRegister = () => {
    if (!fullName.trim()) return "نام کامل الزامی است";
    if (!PHONE_REGEX.test(phoneNumber)) {
      return "شماره موبایل باید با فرمت 09xxxxxxxxx باشد";
    }
    if (!provinceId) return "لطفا استان را انتخاب کنید";
    if (!cityId) return "لطفا شهر را انتخاب کنید";
    if (!address.trim()) return "آدرس الزامی است";
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateRegister();
    if (validationError) {
      toast.error(validationError);
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await mobileRegister({
        fullName: fullName.trim(),
        phoneNumber,
        provinceId: Number(provinceId),
        cityId: Number(cityId),
        address: address.trim(),
      });
      toast.success(REGISTER_SUCCESS_MESSAGE);
      setUsername(phoneNumber);
      setAuthMode("login");
      setError("");
    } catch (err) {
      const errorMessage = err.message || "ثبت نام ناموفق بود";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative w-full bg-white rounded-2xl shadow-2xl">
        {/* Logo — smaller on narrow screens so it doesn't eat viewport height */}
        <div className="absolute -top-10 sm:-top-12 left-1/2 -translate-x-1/2 z-10">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-lg border border-gray-100">
            {settings.app_logo ? (
              <img
                src={settings.app_logo}
                alt={settings.app_name || "Logo"}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <UserIcon className="w-10 h-10 sm:w-12 sm:h-12" />
              </div>
            )}
          </div>
        </div>

        <div className="px-3 pt-12 pb-4 sm:p-5 sm:pt-14 md:p-6 md:pt-14">
          {settings.app_name && (
            <div className="flex flex-col items-center mb-3">
              <h2 className="text-blue-600 text-sm sm:text-base font-semibold text-center px-2 line-clamp-2">
                {settings.app_name}
              </h2>
            </div>
          )}

          {/* Tabs: equal height, readable on all widths */}
          <div
            role="tablist"
            aria-label="حالت ورود"
            className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-4"
          >
            <Button
              type="button"
              role="tab"
              aria-selected={authMode === "login"}
              variant={authMode === "login" ? "default" : "outline"}
              className="h-11 sm:h-10 w-full min-w-0 px-1 sm:px-2 text-[11px] leading-tight sm:text-sm font-medium"
              onClick={() => switchMode("login")}
              disabled={submitting}
            >
              ورود
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={authMode === "otp"}
              variant={authMode === "otp" ? "default" : "outline"}
              className="h-11 sm:h-10 w-full min-w-0 px-1 sm:px-2 text-[11px] leading-tight sm:text-sm font-medium"
              onClick={() => switchMode("otp")}
              disabled={submitting}
              title="ورود با موبایل"
            >
              <span className="sm:hidden">موبایل</span>
              <span className="hidden sm:inline">ورود با موبایل</span>
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={authMode === "register"}
              variant={authMode === "register" ? "default" : "outline"}
              className="h-11 sm:h-10 w-full min-w-0 px-1 sm:px-2 text-[11px] leading-tight sm:text-sm font-medium"
              onClick={() => switchMode("register")}
              disabled={submitting}
            >
              ثبت نام
            </Button>
          </div>

          {authMode === "login" && (
            <form onSubmit={handleUsernameLogin} className="space-y-3">
              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">نام کاربری</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                    <UserIcon className="w-5 h-5" />
                  </span>
                  <Input
                    className="pl-10 text-right"
                    placeholder="نام کاربری"
                    required
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">رمز عبور</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80 hover:text-muted-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                  <Input
                    className="pl-10 text-right"
                    placeholder="رمز عبور"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {error && <div className="text-destructive text-sm text-center">{error}</div>}

              <Button size="lg" className="w-full" type="submit" disabled={submitting}>
                {submitting ? "در حال ورود..." : "ورود"}
              </Button>
            </form>
          )}

          {authMode === "otp" && !verificationToken && (
            <form onSubmit={handleOtpPhoneSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">شماره موبایل</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                    <PhoneIcon className="w-5 h-5" />
                  </span>
                  <Input
                    className="pl-10 text-right"
                    placeholder="09123456789"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={11}
                    value={otpPhone}
                    onChange={(e) => setOtpPhone(normalizePhoneInput(e.target.value))}
                  />
                </div>
              </div>

              {error && <div className="text-destructive text-sm text-center">{error}</div>}

              <Button size="lg" className="w-full" type="submit" disabled={submitting}>
                {submitting ? "در حال ارسال..." : "ارسال کد تایید"}
              </Button>
            </form>
          )}

          {authMode === "otp" && verificationToken && (
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <div className="text-sm text-muted-foreground text-center mb-1">
                <div>
                  کد تایید به شماره <span className="font-medium text-foreground">{otpPhone}</span> ارسال شد
                </div>
                <button
                  type="button"
                  onClick={resetOtpFlow}
                  className="text-xs text-blue-600 hover:underline mt-1 transition-colors"
                  disabled={submitting}
                >
                  ویرایش شماره
                </button>
                {otpTimer > 0 && (
                  <div className="mt-1 text-xs font-medium">
                    زمان باقی‌مانده: {formatTimer(otpTimer)}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">کد تایید</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                    <LockIcon className="w-5 h-5" />
                  </span>
                  <Input
                    className="pl-10 text-center text-lg tracking-widest"
                    placeholder="----"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={MAX_OTP_LENGTH}
                    value={otpCode}
                    onChange={(e) => {
                      const next = normalizeOtpInput(e.target.value);
                      setOtpCode(next);
                      if (next !== lastSubmittedOtpRef.current) {
                        lastSubmittedOtpRef.current = "";
                      }
                    }}
                  />
                </div>
              </div>

              {error && <div className="text-destructive text-sm text-center">{error}</div>}

              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:flex-1 h-11 text-sm"
                  onClick={handleResendOtp}
                  disabled={submitting || otpTimer > 0}
                >
                  {otpTimer > 0 ? `ارسال مجدد (${formatTimer(otpTimer)})` : "ارسال مجدد"}
                </Button>
                <Button
                  size="lg"
                  className="w-full sm:flex-1 h-11"
                  type="submit"
                  disabled={submitting || otpCode.length < MIN_OTP_LENGTH}
                >
                  {submitting ? "در حال تایید..." : "تایید"}
                </Button>
              </div>
            </form>
          )}

          {authMode === "register" && (
            <form onSubmit={handleRegister} className="space-y-2.5 sm:space-y-3">
              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">نام کامل</label>
                <Input
                  className="text-right"
                  placeholder="نام و نام خانوادگی"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">شماره موبایل</label>
                <Input
                  className="text-right"
                  placeholder="09123456789"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={11}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(normalizePhoneInput(e.target.value))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div className="space-y-1.5">
                  <label className="block text-sm text-muted-foreground text-right">استان</label>
                  <Select
                    value={provinceId || undefined}
                    onValueChange={setProvinceId}
                    disabled={loadingProvinces || submitting}
                  >
                    <SelectTrigger className="w-full text-right">
                      <SelectValue placeholder={loadingProvinces ? "در حال بارگذاری..." : "انتخاب استان"} />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm text-muted-foreground text-right">شهر</label>
                  <Select
                    key={provinceId || "no-province"}
                    value={cityId || undefined}
                    onValueChange={setCityId}
                    disabled={!provinceId || loadingCities || submitting}
                  >
                    <SelectTrigger className="w-full text-right">
                      <SelectValue
                        placeholder={
                          !provinceId
                            ? "ابتدا استان"
                            : loadingCities
                              ? "بارگذاری..."
                              : "انتخاب شهر"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm text-muted-foreground text-right">آدرس</label>
                <Textarea
                  className="text-right resize-none min-h-[72px] sm:min-h-[80px]"
                  placeholder="آدرس کامل"
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              {error && <div className="text-destructive text-sm text-center">{error}</div>}

              <Button size="lg" className="w-full" type="submit" disabled={submitting || loadingCities}>
                {submitting ? "در حال ثبت‌نام..." : "ثبت نام"}
              </Button>
            </form>
          )}

          <div className="mt-4 pt-3 border-t border-gray-200 text-center text-xs text-gray-600">
            <div>
              طراحی شده توسط{" "}
              <a
                href="https://bazartalairan.ir/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline transition-colors"
              >
                بازار طلای ایران
              </a>
            </div>
            <div className="mt-1 opacity-70">
              نسخه {packageJson.version}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
